import { CurrencyTypes } from "constants/widget";

import { intlPriceFormat, priceFormat } from "./mathUtils";

export const formatPriceRealCurrency = (exchangeRate, price, locale, symbol) => {
  // eslint-disable-next-line max-len
  if (price === null || price === undefined) {
    return "";
  }
  if (!exchangeRate?.numerator || !exchangeRate?.denominator || !exchangeRate?.code) {
    // In case the user has not configured a conversion rate but wants to display real currency price,
    // we just display euros (instead of "null"). We only get a symbol and not a trigram, so we use
    // priceFormat instead of intlPriceFormat.
    return priceFormat(price, 2, symbol, locale);
  }
  // Otherwise we display the real currency price, based on the virtual price + conversion rate
  return intlPriceFormat(
    (price * exchangeRate.numerator) / exchangeRate.denominator,
    2, exchangeRate.code, locale, true,
  );
};

export const formatPriceVirtualCurrency = (price, nbDecimal, symbol, locale) => (
  priceFormat(price, nbDecimal, symbol, locale)
);

/**
 * Compute the price to display based on the currency type : virtual currency, real currency or both.
 * @param {number} exchangeRate The exchange rate between the real and the virtual currencies.
 * @param {number} price The amount to display.
 * @param {string} nbDecimal The currency decimal places.
 * @param {string} symbol The symbol of the currency to display.
 * @param {string} locale The locale language used to display the price according to the way of the country.
 * @param {string} currencyTypeToDisplay Type of currency to display: virtual currency, real currency or both.
 * @return {string} The price formatted according to the locale, the currency and which currency type to display.
 */
export const formatPrice = (
  exchangeRate,
  price,
  nbDecimal,
  symbol,
  locale,
  currencyTypeToDisplay,
) => {
  const priceVirtualCurrency = currencyTypeToDisplay !== CurrencyTypes.REAL
    ? formatPriceVirtualCurrency(price, nbDecimal, symbol, locale)
    : null;

  const priceRealCurrency = currencyTypeToDisplay !== CurrencyTypes.VIRTUAL
    ? formatPriceRealCurrency(exchangeRate, price, locale, symbol)
    : null;

  if (currencyTypeToDisplay === CurrencyTypes.VIRTUAL_AND_REAL && priceRealCurrency) {
    return `${priceVirtualCurrency} (${priceRealCurrency})`;
  }

  return priceRealCurrency || priceVirtualCurrency;
};

export const formatAmounts = (
  amounts,
  exchangeRate,
  locale,
  currencyTypeToDisplay,
  withoutNumberSign = false,
) => amounts.map((amountDict) => {
  const price = amountDict.currency_group === 1
    // eslint-disable-next-line max-len
    ? formatPrice(exchangeRate, Math.abs(amountDict.amount), amountDict.decimal_places, amountDict.symbol, locale, currencyTypeToDisplay)
    : priceFormat(Math.abs(amountDict.amount), amountDict.decimal_places, amountDict.symbol, locale);
  return withoutNumberSign ? price : `${amountDict.amount > 0 ? "+" : "-"} ${price}`;
}).join(" | ");

export const getTotalDiscounts = (rows) => Object.values(rows.flatMap((row) => [
  ...(row.discountRows || []),
  ...(row.menuRows?.flatMap((menuRow) => menuRow.discounts || []) || []),
]).reduce((acc, discount) => {
  if (!acc[discount.productId]) {
    acc[discount.productId] = {
      name: discount.productName,
      total: discount.total.rawAmount,
      currencyCode: discount.total.currencyCode,
      decimalPlaces: discount.total.decimalPlaces,
    };
  } else {
    acc[discount.productId].total += discount.total.rawAmount;
  }
  return acc;
}, {}));
